import { useRef } from 'react';
import { Modal, Radio, Tooltip } from 'antd';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseInput from 'components/atoms/base-input';
import BaseSwitch from 'components/atoms/base-switch';

import { usePodFormMode } from '../index';
import parentS from '../styles.module.scss';
import s from './styles.module.scss';

import type { ICreatePodFormValues } from '../validation-schema';
import type { FormikProps } from 'formik';
import type { FC } from 'react';

const BasicInfoSection: FC = () => {
  const { t } = useTranslation();
  const { isEditMode } = usePodFormMode();
  const formik = useFormikContext<ICreatePodFormValues>();
  const { values, setFieldValue, errors, touched } = formik;

  const isStatusConfirmShowingRef = useRef(false);

  const handleStatusToggle = (checked: boolean) => {
    if (isStatusConfirmShowingRef.current) return;
    isStatusConfirmShowingRef.current = true;

    const title = checked ? t('labels.activatePod') : t('labels.inactivatePod');
    const content = checked
      ? t('labels.activatePodBody')
      : t('labels.inactivatePodBody');
    const okText = checked
      ? t('labels.activatePodButton')
      : t('labels.inactivatePodButton');

    Modal.confirm({
      title,
      content,
      okText,
      cancelText: t('labels.cancel'),
      autoFocusButton: 'cancel',
      centered: true,
      rootClassName: 'discard-changes-confirm',
      onOk: () => {
        setFieldValue('isActive', checked);
      },
      afterClose: () => {
        isStatusConfirmShowingRef.current = false;
      },
    });
  };

  const podNameError = touched.podName ? errors.podName : undefined;

  return (
    <div className={s.section} data-cy="create-pod__basic-info">
      <div className={parentS.sectionHeader}>
        <span className={parentS.sectionTitle}>{t('labels.basicInfo')}</span>
        <span className={parentS.sectionHelper}>
          {t('labels.basicInfoHelper')}
        </span>
      </div>

      <BaseForm formik={formik as FormikProps<ICreatePodFormValues>}>
        <div className={s.nameActiveRow}>
          <div className={s.podNameField} data-cy="create-pod__pod-name">
            <BaseFormItem
              name="podName"
              label={t('labels.podName')}
              required
              showErrorBlock={false}
            >
              <BaseInput maxLength={255} />
            </BaseFormItem>
            <span className={podNameError ? s.errorText : s.helperText}>
              {podNameError
                ? String(errors.podName)
                : t('labels.podNameHelper')}
            </span>
          </div>
          <div className={s.activeField}>
            <BaseFormItem name="isActive" label={t('labels.active')}>
              <BaseSwitch
                dataCy="create-pod__active-toggle"
                onChange={handleStatusToggle}
              />
            </BaseFormItem>
          </div>
        </div>
      </BaseForm>

      <div className={s.podTypeRow}>
        <span className={s.podTypeLabel}>{t('labels.podType')}</span>
        {isEditMode ? (
          <Tooltip title={t('labels.podTypeCannotBeChanged')}>
            <span>
              <Radio.Group
                name="podType"
                value={values.podType}
                disabled
                aria-label={t('labels.podType')}
                data-cy="create-pod__pod-type"
              >
                <Radio value="RPM">{t('labels.rpm')}</Radio>
                <Radio value="CCM">{t('labels.ccm')}</Radio>
              </Radio.Group>
            </span>
          </Tooltip>
        ) : (
          <Radio.Group
            name="podType"
            value={values.podType}
            onChange={(e) => {
              setFieldValue('podType', e.target.value);
            }}
            aria-label={t('labels.podType')}
            data-cy="create-pod__pod-type"
          >
            <Radio value="RPM">{t('labels.rpm')}</Radio>
            <Radio value="CCM">{t('labels.ccm')}</Radio>
          </Radio.Group>
        )}
      </div>
    </div>
  );
};

export default BasicInfoSection;
